import gsap from 'gsap';
import { Group, Mesh, PlaneBufferGeometry } from 'three';
import { Component3D } from './Component3D';
import { mathSign } from '../../../helpers/sign';
import { MaterialsFactory } from '../factories/MaterialsFactory';
import { materialsLib } from '../../../../data/settings';
import { getPlateGeometry } from '../geometries/PlateGeometry';
import { SIZE_OPTIONS, SIZES_COMMON, UNIT } from '../../../constants/sizes';
import { getTextMetrics } from './metrics/ScoreMetrics';
import Text3D from './Text3D';
import { COLOR_SCORE_TEXT } from '../../../constants/colors';

export class Score3D extends Component3D {
    #value = 0;
    #text3d;
    #arrows3d;
    #arrows3dPosition;
    #arrows3dAbstract;
    #animG;
    #plate;
    #rootG;
    #webgl;
    #arrowHeight;
    _animOpacity = 0;
    #orientation = SIZE_OPTIONS.LANDSCAPE;

    constructor() {
        super();

        const rootG = new Group();
        const animG = new Group();

        const material = MaterialsFactory.create(materialsLib.matcapClay6);
        let geom = getPlateGeometry({
            innerR: UNIT.scoreR,
            outerR: UNIT.scoreR * 1.25,
            height: UNIT.scoreH,
            innerHeight: UNIT.scoreH * 0.65
        });

        geom.rotateX(Math.PI * 0.5);
        geom.translate(0, 0, -UNIT.scoreH * 0.6);

        const plate = new Mesh(geom, material);

        animG.position.z = -UNIT.scoreH * 1.1;
        animG.position.y = -UNIT.scoreR * 1.22;

        this.#arrowHeight = UNIT.scoreR * 1.6;
        geom = new PlaneBufferGeometry(UNIT.scoreR * 1.3, this.#arrowHeight);

        const arrows3d = new Array(4)
            .fill(1)
            .map(() => new Mesh(geom, MaterialsFactory.create(materialsLib.scoreArrowBasic)));

        arrows3d.forEach((obj3d) => {
            obj3d.position.y = this.#arrowHeight * 0.5;
            obj3d.material.opacity = 0;
            animG.add(obj3d);
        });

        this.#plate = plate;
        this.#animG = animG;
        this.#arrows3d = arrows3d;
        this.#arrows3dPosition = arrows3d.map((obj3d) => obj3d.position);
        this.#arrows3dAbstract = arrows3d.map((obj3d) => ({ material: obj3d.material, opacity: 0 }));
        this.#rootG = rootG;

        rootG.add(plate);
        rootG.add(animG);
        this.add(rootG);
    }

    _setup() {
        super._setup();

        this.waitForPreload = true;
        this.hasResize = true;
        this.hasStart = true;
    }

    stop() {
        if (this._tl) this._tl.kill();
    }

    _resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio) {
        super._resize(winWidth, winHeight, orientation, orientationChanged, changed, pixelRatio);

        this.#orientation = orientation;

        if (changed) {
            if (this.#text3d) {
                const metricsTxt = getTextMetrics(orientation);

                this.#text3d.setMetrics({
                    ...metricsTxt,
                    valign: 'center'
                });
            }

            // MOVE TO REF SIZES
            if (winWidth > winHeight) {
                this.#rootG.position.x = UNIT.refSize * (0.99 + 0.5 * ((winWidth - winHeight) / winHeight));
            } else {
                this.#rootG.position.x = UNIT.refSize * 0.85;
            }

            if (orientationChanged) {
                let s = 2;

                if (orientation === SIZE_OPTIONS.PORTRAIT) {
                    this.#rootG.position.y = UNIT.refSize * 0.75;
                    s = 1.6;
                } else if (orientation === SIZE_OPTIONS.MOBILE_PORTRAIT) {
                    this.#rootG.position.y = UNIT.refSize * 0.85;
                    s = 2.2;
                } else {
                    this.#rootG.position.y = UNIT.refSize * 0.5;
                }
                // Due to z position
                this.#rootG.scale.set(s, s, s);
            }

            this.#rootG.position.z = -UNIT.refSize * 0.75;
        }

        this.updatePointOfInterest();
    }

    updatePointOfInterest() {
        this.#rootG.lookAt(this.position.x - SIZES_COMMON.camDistance * 0.2, this.position.y, this.position.z + SIZES_COMMON.camDistance);
    }

    setupPreloadedContent(infrastructure) {
        infrastructure = infrastructure || this.#webgl;

        if (!infrastructure) return;

        const metricsTxt = getTextMetrics(this.#orientation);

        const text3d = new Text3D({
            ...metricsTxt,
            color: COLOR_SCORE_TEXT,
            valign: 'center'
        }, this.#value.toString(), infrastructure);

        this.#rootG.add(text3d);

        this.#text3d = text3d;
        this.#webgl = infrastructure;
    }

    updateValue(val, direction, immediate = false) {
        if (this.#value === val) return;

        const sign = mathSign(direction);

        if (sign > 0) {
            this.#playUpAnimation();
        } else if (sign < 0) {
            this.#playDownAnimation();
        }

        this.#value = val;

        if (this.#text3d) {
            this.#rootG.remove(this.#text3d);
            this.setupPreloadedContent();
        }

        if (!immediate) this.showAnimation();
    }

    showAnimation() {
        gsap.killTweensOf(this);
        gsap.to(this, {
            animOpacity: 1,
            duration: 0.4
        });
    }

    hideAnimation() {
        gsap.killTweensOf(this);
        gsap.to(this, {
            animOpacity: 0,
            duration: 0.6
        });
    }

    #playUpAnimation() {
        this.#animG.position.y = UNIT.scoreR * 1.22;
        this.#animG.rotation.z = Math.PI;

        this.#play();
    }

    #playDownAnimation() {
        this.#animG.position.y = -UNIT.scoreR * 1.22;
        this.#animG.rotation.z = 0;

        this.#play();
    }

    #play() {
        if (this._tl) this._tl.kill();

        const tl = gsap.timeline({
            onUpdate: () => {
                this._updateOpacity();
            }
        });
        const amount = 1.5;
        const duration = 1.2 * amount;

        tl.addLabel('start', 0);
        tl.fromTo(
            this.#arrows3dPosition,
            {
                y: this.#arrowHeight * 0.5
            },
            {
                duration,
                y: -this.#arrowHeight,
                ease: 'power1.inOut',
                stagger: {
                    grid: [1, 4],
                    amount,
                    repeat: -1
                }
            },
            'start');
        tl.fromTo(
            this.#arrows3dAbstract,
            {
                opacity: 1
            },
            {
                duration,
                opacity: 0,
                ease: 'power1.in',
                stagger: {
                    grid: [1, 4],
                    amount,
                    repeat: -1
                }
            },
            'start');

        this._tl = tl;
    }

    set animOpacity(val) {
        this._animOpacity = val;
        this._updateOpacity();
    }

    get animOpacity() {
        return this._animOpacity;
    }

    _updateOpacity() {
        this.#arrows3dAbstract.forEach(({ opacity, material }) => {
            material.opacity = opacity * this._animOpacity;
        });
    }
}
